<template>
  <v-card class="ma-4">
    <v-toolbar>
      <v-toolbar-title>{{ eventTitle }}</v-toolbar-title>
      <span class="ml-4 mt-1">on {{ eventDate }}</span>
      <v-spacer />
    </v-toolbar>
    <v-tabs v-model="tab">
      <v-tab>Event Info</v-tab>
      <v-tab>Students</v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item>
        <v-card-text>
          <v-row>
            <v-col :cols="12" md="6">
              <p>Event Description. This can include some of the duties students will be performing, or any other information that would be relevant for the students to see. You may leave this blank if nothing is necessary.</p>
              <html-editor v-model="description" label="Description" />
              <p>Please enter the date range for when students can sign up for this event.</p>
              <v-row>
                <v-col>
                  <date-picker v-model="regOpen" label="Sign-Up Open" outlined />
                </v-col>
                <v-col>
                  <date-picker v-model="regClosed" label="Sign-Up Closed" :max="eventDate" outlined />
                </v-col>
              </v-row>
            </v-col>
            <v-col :cols="12" md="6">
              <h3>Duties/Selectable Options</h3>
              <p>This is where you can specify the options within your event that someone can sign up to do. If you only have one option, just enter the one, and there's no limit to how many you can add here.</p>
              <v-list>
                <v-list-item v-for="{ _id, desc, slotCount, slotsFilled, minHours, maxHours } of filteredBlocks" :key="_id">
                  <v-list-item-content>
                    <v-list-item-title>{{ desc }}</v-list-item-title>
                    <v-list-item-subitle>Slots: {{ slotsFilled }} out of {{ slotCount }}</v-list-item-subitle>
                    <v-list-item-subtitle v-if="minHours === maxHours">{{ minHours }} Hour{{ minHours === 1 ? '' : 's' }}</v-list-item-subtitle>
                    <v-list-item-subtitle v-else>{{ minHours }} to {{ maxHours }} Hours</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="#193264" style="color:white;">{{ slotCount - slotsFilled }} Left</v-chip>
                  </v-list-item-action>
                  <v-list-item-action>
                    <v-btn icon @click="editBlock(_id)">
                      <v-icon small>fal fa-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <v-btn @click="addBlock()">Add Option</v-btn>
            </v-col>
            <v-dialog v-model="blockDialog" width="400">
              <v-card>
                <v-card-title>{{ block._id ? 'Edit' : 'Add' }} Option</v-card-title>
                <v-card-text>
                  <p>Enter the description for this sign-up option. If there is a specific time for this, please make sure to include that.</p>
                  <v-text-field v-model="block.desc" label="Description" outlined hide-details />
                  <p>Enter how many students would be allowed to sign up for this option.</p>
                  <v-text-field v-model="block.slotCount" label="Student Count" type="number" outlined hide-details />
                  <p>Enter the range of how many hours a student can sign up for when selecting this option. If it's a fixed number of hours, enter the same value for both min and max.</p>
                  <v-row>
                    <v-col>
                      <v-text-field v-model="block.minHours" label="Min Hours" type="number" outlined hide-details />
                    </v-col>
                    <v-col>
                      <v-text-field v-model="block.maxHours" label="Max Hours" type="number" outlined hide-details />
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-btn @click="saveBlock()">{{ block._id ? 'Update' : 'Add' }} Option</v-btn>
                  <v-spacer />
                  <v-btn @click="blockDialog = false">Cancel</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-row>
        </v-card-text>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>
<script>
import { ref, computed, onMounted } from '@vue/composition-api'

export default {
  components: {
    DatePicker: () => import('@/components/forms/inputs/DatePicker'),
    // EmailDialog: () => import('@/components/system/emailDialog'),
    HtmlEditor: () => import('@/components/forms/HTMLEditor')
  },
  setup (props, { root }) {
    const id = computed(() => root.$route.params.id)
    const eventTitle = ref('')
    const eventDate = ref('')
    const tab = ref(0)
    const description = ref('')
    const regOpen = ref('')
    const regClosed = ref('')
    const students = ref([])

    const blockDialog = ref(false)
    const blockFilter = ref('')
    const blocks = ref([])
    const filteredBlocks = computed(() => blocks.value.map((obj) => {
      const slotsFilled = students.value.filter(({ blocks }) => blocks.includes(obj._id)).length
      return { ...obj, slotsFilled }
    }).filter(({ slotCount, slotsFilled }) => {
      if (blockFilter.value === 'filled') return slotsFilled === slotCount
      else if (blockFilter.value === 'over') return slotsFilled > slotCount
      else if (blockFilter.value === 'empty') return slotsFilled === 0
      else if (blockFilter.value === 'under') return slotsFilled < slotCount
      return true
    }))
    const block = ref({
      desc: '',
      slotCount: 0,
      minHours: 0,
      maxHours: 10
    })
    function resetBlock () {
      block.value = {
        desc: '',
        slotCount: 0,
        minHours: 0,
        maxHours: 10
      }
    }
    function addBlock () {
      resetBlock()
      blockDialog.value = true
    }
    function editBlock (id) {
      const rec = blocks.value.find(({ _id }) => _id === id)
      if (rec) {
        block.value = { ...rec }
        blockDialog.value = true
      }
    }
    async function saveBlock () {
      if (block.value._id) {
        const patch = {
          'blocks.$[el].desc': block.value.desc,
          'blocks.$[el].slotCount': block.value.slotCount,
          'blocks.$[el].minHours': block.value.minHours,
          'blocks.$[el].maxHours': block.value.maxHours
        }
        const arrayFilters = [{ 'el._id': block.value._id }]
        const res = await root.$feathers.service('serve/event').patch(id.value, patch, { query: { arrayFilters } })
        blocks.value = res.blocks
      } else {
        const res = await root.$feathers.service('serve/event').patch(id.value, { $push: { blocks: block.value } })
        blocks.value = res.blocks
      }
      blockDialog.value = false
      resetBlock()
    }

    onMounted(async () => {
      const rec = await root.$feathers.service('serve/event').get(id.value)
      console.log(rec)
      eventTitle.value = rec.name
      eventDate.value = rec.date
      description.value = rec.description
      regOpen.value = rec.regOpen
      regClosed.value = rec.regClosed
      if (rec.blocks) blocks.value = rec.blocks
      if (rec.students) students.value = rec.students
    })

    return {
      id,
      eventTitle,
      eventDate,
      tab,
      description,
      regOpen,
      regClosed,
      blockDialog,
      blocks,
      blockFilter,
      filteredBlocks,
      block,
      resetBlock,
      addBlock,
      editBlock,
      saveBlock
    }
  }
}
</script>
